import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Theme hook — dark ↔ light with per-user cross-device sync.
 *
 * Storage: this hook reads/writes `yadidos.theme` in localStorage.
 * NOTE (security review): the localStorage layer here stores ONLY a
 * non-sensitive UI preference ("dark" or "light"). It is not an auth token,
 * PII, or firm data. Even so, we validate on read via `_clean(...)` so a
 * poisoned localStorage value can never render as an arbitrary CSS attribute
 * or shell out of the whitelisted set. The authoritative source of truth is
 * the server (`/users/me/preferences`); localStorage is a first-paint cache
 * and offline shim.
 *
 * Values: "dark" (default) | "light".
 * Applied as `<html data-theme="…">`. The two palettes live in `index.css`
 * as CSS-variable overrides on `:root` (dark) and `html[data-theme="light"]`
 * respectively. They are DELIBERATELY not inversions of each other:
 *   • Dark  — Navy Ink + near-black surfaces, Ivory-toned text.
 *   • Light — Ivory reading surface, Navy structure, near-black text.
 *
 * Persistence layers (identical pattern to font_scale / layout_density):
 *   1. localStorage `yadidos.theme` — instant paint, offline-safe.
 *   2. `PUT /users/me/preferences {theme}` — follows partner across devices.
 */
const STORAGE_KEY = "yadidos.theme";
const THEMES = ["dark", "light"];
const DEFAULT = "dark";

function _clean(v) {
  const s = String(v || "").toLowerCase().trim();
  return THEMES.includes(s) ? s : DEFAULT;
}

function _readLocal() {
  try { return _clean(localStorage.getItem(STORAGE_KEY)); }
  catch (_) { return DEFAULT; }
}

function _apply(theme) {
  document.documentElement.setAttribute("data-theme", theme);
  // Also toggle Tailwind's built-in `dark` class since some third-party
  // shadcn presets key off it. Keeps existing components consistent.
  if (theme === "dark") document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
}

if (typeof window !== "undefined") {
  try { _apply(_readLocal()); } catch (e) { console.warn("theme: initial apply failed", e); }
}

export function useTheme() {
  const { user, mergePreferences } = useAuth();
  const [theme, setThemeState] = useState(_readLocal);
  const lastServerRef = useRef(null);

  useEffect(() => { _apply(theme); }, [theme]);

  useEffect(() => {
    const s = user?.preferences?.theme;
    if (!s) return;
    const clean = _clean(s);
    if (lastServerRef.current === clean) return;
    lastServerRef.current = clean;
    if (clean !== _readLocal()) {
      try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) { console.warn("theme: localStorage write failed (server hydrate)", e); }
      setThemeState(clean);
    }
  }, [user]);

  const setTheme = useCallback((next) => {
    const clean = _clean(next);
    try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) { console.warn("theme: localStorage write failed (user set)", e); }
    setThemeState(clean);
    // Keep `lastServerRef` in sync so the auth-context re-emit that follows
    // mergePreferences doesn't fire the "server said different" branch.
    lastServerRef.current = clean;
    api.put("/users/me/preferences", { theme: clean })
      .then(() => mergePreferences?.({ theme: clean }))
      .catch((e) => console.warn("theme sync failed", e));
  }, [mergePreferences]);

  const toggle = useCallback(() => setTheme(theme === "dark" ? "light" : "dark"), [theme, setTheme]);
  const syncedToServer = typeof user?.preferences?.theme === "string";

  return { theme, setTheme, toggle, syncedToServer };
}
